import type { ReactNode } from 'react';
import type { Build } from '@/api/schemas';
import { useBreakpoint } from '@/lib/useBreakpoint';
import { useNow } from '@/lib/useNow';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import type { Can } from '@/lib/permissions';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/ui/button';
import { BuildCard } from './BuildCard';
import { BuildsTable } from './BuildsTable';
import { CardsSkeleton, TableSkeleton } from './BuildsSkeletons';
import { listView, noBuildsDescription } from './buildsListModel';

/** Gate of the empty state's Run build (useRunBuildGate): hidden for guests, disabled with its reason otherwise. */
type RunBuildGate = { can: Can; blockedReason?: string };

interface Props {
  projectSlug: string;
  builds: Build[] | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
  hasMore: boolean;
  isFetchingMore: boolean;
  onLoadMore: () => void;
  filtered: boolean;
  onRunBuild: () => void;
  runBuildGate?: RunBuildGate;
  onReset: () => void;
}

export function BuildsList(props: Props) {
  const phone = useBreakpoint() === 'phone';
  const now = useNow(5_000);
  const view = listView(props);
  if (view.kind === 'loading') return phone ? <CardsSkeleton /> : <TableSkeleton />;
  if (view.kind === 'error') return <ErrorState compact error={props.error} onRetry={props.onRetry} />;
  if (view.kind === 'empty') return <EmptyList filtered={props.filtered} onReset={props.onReset} onRunBuild={props.onRunBuild} runBuildGate={props.runBuildGate} />;
  const loadMore = <LoadMore hasMore={props.hasMore} loading={props.isFetchingMore} onLoadMore={props.onLoadMore} />;
  if (phone) return <CardsList builds={view.builds} now={now} projectSlug={props.projectSlug} loadMore={loadMore} />;
  return (
    <>
      <div className="@container">
        <BuildsTable builds={view.builds} now={now} projectSlug={props.projectSlug} />
      </div>
      {loadMore}
    </>
  );
}

function CardsList({ builds, now, projectSlug, loadMore }: { builds: Build[]; now: number; projectSlug: string; loadMore: ReactNode }) {
  return (
    <>
      <ul className="flex flex-col gap-3 p-4">
        {builds.map((b, i) => (
          <BuildCard key={b.id} build={b} index={Math.min(i, 10)} now={now} projectSlug={projectSlug} />
        ))}
      </ul>
      {loadMore}
    </>
  );
}

function LoadMore({ hasMore, loading, onLoadMore }: { hasMore: boolean; loading: boolean; onLoadMore: () => void }) {
  if (!hasMore) return null;
  return (
    <div className="flex justify-center border-t p-3">
      <Button variant="outline" data-testid="builds-load-more" loading={loading} onClick={onLoadMore}>
        Load more
      </Button>
    </div>
  );
}

function EmptyList({ filtered, onReset, onRunBuild, runBuildGate }: { filtered: boolean; onReset: () => void; onRunBuild: () => void; runBuildGate?: RunBuildGate }) {
  if (!filtered) return <NoBuildsYet onRunBuild={onRunBuild} runBuildGate={runBuildGate} />;
  return (
    <EmptyState
      testId="builds-empty"
      icon="search"
      title="No builds match these filters"
      description="Try a wider status set, another environment or clear the search."
      action={
        <Button variant="outline" data-testid="builds-empty-reset" onClick={onReset}>
          Clear filters
        </Button>
      }
    />
  );
}

function NoBuildsYet({ onRunBuild, runBuildGate }: { onRunBuild: () => void; runBuildGate?: RunBuildGate }) {
  return (
    <EmptyState
      testId="builds-empty"
      icon="construction"
      title="No builds yet"
      description={noBuildsDescription(runBuildGate?.blockedReason)}
      action={runBuildGate?.can.hidden ? undefined : <RunBuildAction onRunBuild={onRunBuild} runBuildGate={runBuildGate} />}
    />
  );
}

function RunBuildAction({ onRunBuild, runBuildGate }: { onRunBuild: () => void; runBuildGate?: RunBuildGate }) {
  return (
    <GatedButton data-testid="builds-empty-run" can={runBuildGate?.can} blockedReason={runBuildGate?.blockedReason} onClick={onRunBuild}>
      <Icon name="add" size={18} />
      Run build
    </GatedButton>
  );
}
